/** Section 3.2 - the battle kernel's single authoritative entry point.
 *
 * `executeBattleCommand` mutates `state` IN PLACE and journals every write
 * (D1). A command that turns out to be illegal is rolled back to the mark taken
 * on entry, so an `Err` return leaves the state byte-identical to what it was:
 * illegality is returned, never thrown, and never half-applied.
 *
 * Turn machine, in the order it runs:
 *   settle -> begin turn (clear defend, roll bad morale) -> player command
 *   -> resolve -> deaths -> victory check -> good-morale extra action
 *   -> advance -> settle -> (round boundary: reorder, expire, regenerate)
 */

import type { BattleCommand, ExecuteError } from "../commands.js";
import { asModifierSrcId, type CreatureDefId } from "../ids.js";
import { startCount } from "./revenge.js";
import { flagsForCreature } from "./creature.js";
import { addStack, freeHexFor } from "./summon.js";
import { illegal, malformed } from "../commands.js";
import type { GameEvent } from "../events.js";
import { Err, Ok, type Result } from "../result.js";
import { type StateHash, hashState } from "../hash/index.js";
import { FP_ONE, clampInt, roundHalfAway } from "../numeric.js";
import type { StatKey } from "../modifiers.js";
import { type ContentRegistry, type CreatureAbility, must } from "../content/registry.js";
import {
  BF_HEXES, F_ALIVE, F_BERSERK, F_BLINDED, F_DEFENDING, F_MOVED, F_NO_RETALIATION,
  F_PETRIFIED, F_RETALIATED, F_TWO_HEX, F_UNDEAD, F_WAITED, F_WAR_MACHINE,
  SIDE_ATTACKER, SIDE_DEFENDER,
  type BattleState, type Obstacle, hexCol, hexDistance, inTacticsBand, isAlive,
  tacticsSide, F_NON_LIVING,
} from "./state.js";
import {
  BALLISTICS_BITE, BALLISTICS_HITS, FIRST_AID_HEAL,
  batterCrewLevel,
  canBatter, canShoot, canTend, crewLevel, isCrewed, isUntargetable, isWarMachine,
  sideHasAmmoCart,
} from "./machines.js";
import {
  F_ACTED, F_CLONE, F_HYPNOTIZED, F_MORALE_USED,
  F2_AFRAID, F2_BOUND, F2_MEDITATED, F2_MEDITATING, F2_PREEMPTED,
  F_RETAL_MASK, F_RETAL_SHIFT,
  F_TURN_STARTED, ROUND_SCOPED_FLAGS, ROUND_SCOPED_FLAGS2, retaliationsUsed, F_SUMMONED,
} from "./flags.js";
import {
  type BattleJournal, type JournalMark, forkBattle, getJournal, jSetField, jSetI32, undoTo,
} from "./journal.js";
import { activeStack, writeRoundOrder, reorderForWait } from "./order.js";
import { SRC_RUNE_TURN, gainRunes } from "./runes.js";
import { canDevour, devour, spendBankedStrike } from "./devour.js";
import { heatStrokeHexes } from "./heatstroke.js";
import { stackHexes } from "./move.js";
import {
  DEFENSE_REDUCTION_CAP_FP, DEFENSE_STEP_FP, SRC_DEFEND, computeDamage,
  defendModifier, effectiveDefense, rollBaseDamage,
} from "./damage.js";
import {
  UNREACHABLE, computeReach, hazardBite, inHardCover, meleeBlocked, newOccupancy,
  canOccupy, obstacleAt, pathTo, shotCrossesWall, stacksAdjacent, tailHex, wouldBeAdjacent,
} from "./move.js";
import {
  type Ctx, addMod, clearFlags, clearFlags2, creditKills, damageStack,
  expireRoundMods, healStack, removeModsBySource, rollBounded, setFlags, setFlags2, setHex,
} from "./apply.js";
import {
  abilitiesOf, abilityOf, hasAbility, onDamageAbilities, onRoundStartAbilities,
  onUnitDeathAbilities, shapeAttack, wakeOnHit, SRC_PETRIFY,
} from "./abilities.js";
import {
  STAT_BERSERK, STAT_CLONE, STAT_EXTRA_RETALIATION, STAT_HYPNOSIS,
  STAT_REGEN,
  castCreatureSpell, castHeroSpell, escapeBarred, castAfterShot, magicStrikeFp } from "./spells.js";
import { combatLuck, stackFortune } from "./wet.js";
import { STAT_BLIND } from "./stats.js";
import { heroStrike } from "./heroStrike.js";
import { racialOf, spendRacial } from "./racial.js";
import { activate } from "./activate.js";
import {
  SRC_BOARDING, SRC_CANOPY, applyShieldWall, boardingIgnoreBp, canopyHalves,
  cindersBonus, miasmaPenalty, onPerkKill, perkRoundStart, rallyOnDeath,
} from "./armyPerk.js";

export interface CommandResult<S> {
  readonly state: S;
  readonly events: readonly GameEvent[];
  readonly hash?: StateHash;
}

export interface BattleExecuteOpts {
  readonly verify?: boolean;
}

/** The expansion's template default; a battle that reaches it is a draw. */
export const ROUND_LIMIT = 100;
/** Castle moat damage, applied once on entry. */
export const MOAT_DAMAGE = 90;
/** Field Hospital: hit points mended per turn, before the hero's spell power. */
export const TENT_HEAL = 25;

// -------------------------------------------------------------- turn machine

/**
 * Creatures still standing on a side. War machines are deliberately not
 * counted: they are equipment, and a hero whose last creature has fallen has
 * lost the battle even though his catapult is untouched - counting them would
 * leave an untargetable breaker keeping a decided battle open for ever.
 */
function aliveCount(s: BattleState, side: number): number {
  let n = 0;
  for (let i = 0; i < s.stacks.n; i++) {
    if (!isAlive(s.stacks, i)) continue;
    if (isWarMachine(s.stacks, i)) continue;
    // A hypnotised stack fights for the other side and still *belongs* to the
    // one that brought it: an army whose last stack has been charmed has not
    // lost the battle, it has lost control of a stack. Counting it where it
    // stands would end the fight the moment the spell landed, and hand the
    // caster a win he has not had to finish.
    const owner = (s.stacks.flags[i]! & F_HYPNOTIZED) !== 0
      ? (s.stacks.side[i] === 0 ? 1 : 0)
      : s.stacks.side[i];
    if (owner !== side) continue;
    n++;
  }
  return n;
}

function checkVictory(c: Ctx): boolean {
  const s = c.s;
  if (s.outcome.kind !== "ongoing") return true;
  const a = aliveCount(s, SIDE_ATTACKER);
  const d = aliveCount(s, SIDE_DEFENDER);
  if (a > 0 && d > 0) return false;
  const outcome: BattleState["outcome"] =
    a === 0 && d === 0 ? { kind: "draw" }
      : a === 0 ? { kind: "victory", side: SIDE_DEFENDER }
        : { kind: "victory", side: SIDE_ATTACKER };
  jSetField(c.j, s, "outcome", outcome);
  c.events.push({
    type: "BattleEnded",
    winner: outcome.kind === "victory" ? outcome.side : null,
    kind: outcome.kind,
  });
  return true;
}

function moraleEligible(s: BattleState, slot: number, content: ContentRegistry): boolean {
  const f = s.stacks.flags[slot]!;
  if ((f & (F_UNDEAD | F_WAR_MACHINE)) !== 0) return false;
  return !hasAbility(content, s.stacks.def[slot]!, "FEARLESS");
}

/**
 * Morale trigger chances are the classic ones: each point of positive morale is
 * 1-in-24 for an extra action, each point of negative morale 1-in-12 for a lost
 * one. They are rolled as bounded integers rather than basis points so the
 * twelfths are exact.
 */
function rollGoodMorale(c: Ctx, content: ContentRegistry, slot: number): boolean {
  const m = clampInt(stackFortune(c.s, slot, "morale"), -3, 3);
  if (m <= 0 || !moraleEligible(c.s, slot, content)) return false;
  return rollBounded(c, "battle.morale", 24) < m;
}

function rollBadMorale(c: Ctx, content: ContentRegistry, slot: number): boolean {
  const m = clampInt(stackFortune(c.s, slot, "morale"), -3, 3);
  if (m >= 0 || !moraleEligible(c.s, slot, content)) return false;
  return rollBounded(c, "battle.morale", 12) < -m;
}

/** +1 lucky, -1 unlucky, 0 neither. The expansion rolls it per damaged target. */
export function rollLuck(c: Ctx, slot: number): -1 | 0 | 1 {
  const l = combatLuck(c.s, slot);
  if (l > 0) return rollBounded(c, "battle.luck", 24) < l ? 1 : 0;
  if (l < 0) return rollBounded(c, "battle.luck", 12) < -l ? -1 : 0;
  return 0;
}

function startRound(c: Ctx, content: ContentRegistry): void {
  const s = c.s;
  if (s.round >= ROUND_LIMIT) {
    jSetField(c.j, s, "outcome", { kind: "draw" } as BattleState["outcome"]);
    c.events.push({ type: "BattleEnded", winner: null, kind: "draw" });
    return;
  }
  applyShieldWall(c, content);
  jSetField(c.j, s, "round", s.round + 1);
  for (let i = 0; i < s.stacks.n; i++) {
    jSetI32(c.j, s.stacks.flags, i, s.stacks.flags[i]! & ~ROUND_SCOPED_FLAGS);
    jSetI32(c.j, s.stacks.flags2, i, s.stacks.flags2[i]! & ~ROUND_SCOPED_FLAGS2);
    if (s.stacks.cooldown[i]! > 0) jSetI32(c.j, s.stacks.cooldown, i, s.stacks.cooldown[i]! - 1);
    jSetI32(c.j, s.waited, i, 0);
    removeModsBySource(c, i, SRC_DEFEND);
    // A Blind that runs out this round takes its flag with it; asked before
    // the expiry because afterwards nothing says whether there was one.
    const blindEnds = (s.stacks.flags[i]! & F_BLINDED) !== 0 && s.stacks.mods[i]!.some((m) =>
      m.stat === STAT_BLIND && m.expires?.kind === "round" && m.expires.rounds <= 1);
    expireRoundMods(c, i);
    if (blindEnds) clearFlags(c, i, F_BLINDED);
  }
  for (const h of s.heroes) jSetField(c.j, h as unknown as object, "castThisRound", false);
  writeRoundOrder(s, c.j);
  jSetField(c.j, s, "turnIndex", 0);
  c.events.push({ type: "BattleRoundStarted", round: s.round });
  for (let i = 0; i < s.stacks.n; i++) onRoundStartAbilities(c, content, i);
  perkRoundStart(c, content);
  lapseConjured(c);
  fireTowers(c);
}

// ------------------------------------------------------ conjured, and gone
//
// Three things a spell makes go away by themselves, all ticked at the top of a
// round and all read off state rather than off a timer of their own: a barrier
// counts down its `integrity`, and a clone and a hypnotised stack lapse when
// the modifier the spell left on them does. The modifier is the clock, so the
// duration lives in exactly one place - the spell - and nothing here knows how
// long anything lasts.

/** The mastery behind a stack's Blind (1..4), or 0 when it is not spell-blind. */
function blindMastery(st: BattleState["stacks"], slot: number): number {
  let level = 0;
  for (const m of st.mods[slot]!) if (m.stat === STAT_BLIND && m.value > level) level = m.value;
  return level;
}

function statusStands(st: BattleState["stacks"], slot: number, stat: StatKey): boolean {
  for (const m of st.mods[slot]!) if (m.stat === stat) return true;
  return false;
}

function lapseConjured(c: Ctx): void {
  const s = c.s;

  if (s.obstacles.length > 0) {
    const keep: Obstacle[] = [];
    let changed = false;
    for (const o of s.obstacles) {
      // A mine and a patch of sinking_sand wait to be found rather than burning
      // down, so only the two that stand for a number of rounds tick.
      if (o.kind !== "firewall" && o.kind !== "forcefield") { keep.push(o); continue; }
      const left = o.integrity - 1;
      changed = true;
      if (left > 0) keep.push({ ...o, integrity: left });
    }
    if (changed) jSetField(c.j, s as unknown as object, "obstacles", keep);
  }

  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    const f = st.flags[i]!;
    if ((f & F_CLONE) !== 0 && isAlive(st, i) && !statusStands(st, i, STAT_CLONE)) {
      // The phantom does not die, it stops having been there: no death
      // abilities, no necromancy, no casualty.
      jSetI32(c.j, st.count, i, 0);
      jSetI32(c.j, st.topHp, i, 0);
      jSetI32(c.j, st.flags, i, f & ~F_ALIVE);
      c.events.push({ type: "BattleStackDied", stack: i });
      continue;
    }
    if ((f & F_HYPNOTIZED) !== 0 && !statusStands(st, i, STAT_HYPNOSIS)) {
      jSetI32(c.j, st.side, i, st.side[i] === 0 ? 1 : 0);
      jSetI32(c.j, st.facing, i, -st.facing[i]!);
      jSetI32(c.j, st.flags, i, f & ~F_HYPNOTIZED);
    }
    // The madness passes. Read after the hypnosis arm because a stack can be
    // both, and losing the side-swap first is the order a player would expect.
    if ((st.flags[i]! & F_BERSERK) !== 0 && !statusStands(st, i, STAT_BERSERK)) {
      jSetI32(c.j, st.flags, i, st.flags[i]! & ~F_BERSERK);
    }
  }
}

// ----------------------------------------------------------- arrow towers

/**
 * Section 33 - the turrets shoot.
 *
 * A besieged town's towers were drawn, battered and destroyed, and never once
 * fired: the fort bought the defender masonry and no arrows. They fire once at
 * the top of every round, before anyone moves, which is what makes a long
 * siege expensive rather than merely slow - an attacker who spends six rounds
 * getting through the gate pays six volleys for it.
 *
 * A tower is a wall segment and not a stack, so it has no attack of its own and
 * no damage roll: what it fires is the defending hero's own gunnery. The shot
 * is then reduced by the target's defence on the same curve every other blow
 * uses, so a well-armoured stack shrugs off a turret exactly as far as it
 * shrugs off an archer.
 */
export const TOWER_DAMAGE_BASE = 15;
export const TOWER_DAMAGE_PER_ATTACK = 2;

/** The fattest live attacker, ties to the lower slot; the breaker is exempt. */
function towerTarget(s: BattleState): number {
  const st = s.stacks;
  let best = -1;
  let bestPool = -1;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== SIDE_ATTACKER) continue;
    if (isUntargetable(st, i)) continue;
    const pool = (st.count[i]! - 1) * st.maxHp[i]! + st.topHp[i]!;
    if (pool > bestPool) { bestPool = pool; best = i; }
  }
  return best;
}

function fireTowers(c: Ctx): void {
  const s = c.s;
  if (s.siege === null || !s.siege.active) return;
  const gunner = s.heroes[SIDE_DEFENDER].attack;
  for (const o of s.obstacles) {
    if (o.kind !== "tower" || o.integrity <= 0) continue;
    const target = towerTarget(s);
    if (target < 0) return;
    const base = TOWER_DAMAGE_BASE + gunner * TOWER_DAMAGE_PER_ATTACK;
    const def = effectiveDefense(s, target);
    const red = Math.min(def * DEFENSE_STEP_FP, DEFENSE_REDUCTION_CAP_FP);
    const dealt = base - roundHalfAway((base * red) / FP_ONE);
    const r = damageStack(c, target, dealt < 1 ? 1 : dealt);
    // Reported the way the moat reports itself: nothing charged, so attacker
    // and target are the same slot and the renderer draws damage landing.
    c.events.push({
      type: "BattleAttacked", attacker: target, target,
      damage: r.dealt, killed: r.killed, ranged: true, retaliation: false,
    });
  }
}

/** Is this exact source already on the stack? */
function hasModFrom(s: BattleState, slot: number, source: string): boolean {
  for (const m of s.stacks.mods[slot] ?? []) if (m.source === source) return true;
  return false;
}

/**
 * REPAIR: field engineers mend the constructs and machines beside them at the
 * start of their turn - `hpPerUnit` per engineer, wounds only, never a new
 * head. Passive on purpose: the ability shipped with content and no reader,
 * and a heal that asks for a command would need a verb the AI and the UI
 * both learn; a mend that simply happens is a rule today.
 */
function repairMachines(c: Ctx, content: ContentRegistry, slot: number): void {
  const st = c.s.stacks;
  const a = abilityOf(content, st.def[slot]!, "REPAIR");
  if (a === null) return;
  const amount = a.hpPerUnit * st.count[slot]!;
  if (amount <= 0) return;
  for (let i = 0; i < st.n; i++) {
    if (i === slot || !isAlive(st, i) || st.side[i] !== st.side[slot]) continue;
    if ((st.flags[i]! & (F_NON_LIVING | F_WAR_MACHINE)) === 0) continue;
    if (!stacksAdjacent(st, i, slot)) continue;
    healStack(c, i, amount, st.count[i]!);
  }
}

/** The Summoner has spent its once-a-battle raising. */
export const SRC_SUMMONER_SPENT = asModifierSrcId("ability.summoner.spent");
/** This corpse has already been raised from; a second Summoner finds bones. */
export const SRC_CORPSE_RAISED = asModifierSrcId("ability.summoner.raised");
const STAT_SUMMONER = "battle.summoner" as StatKey;

/**
 * SUMMONER (Pyre Warden): once a battle, at the start of its turn, the richest
 * fallen stack on its own side rises as the creature the ability names - as
 * many as the corpse's hit points buy, never more than fell. The classic makes this a
 * button aimed at a corpse; here it fires on its own, for the same reason
 * REPAIR does. The demons are summoned, not recruited: they do not walk off
 * the field, which is the classic rule for Summon Demons too.
 */
function summonDemons(c: Ctx, content: ContentRegistry, slot: number): void {
  const st = c.s.stacks;
  const a = abilityOf(content, st.def[slot]!, "SUMMONER");
  if (a === null || hasModFrom(c.s, slot, SRC_SUMMONER_SPENT as string)) return;
  const demon = content.creatures.get(a.creature);
  if (demon === undefined) return;
  const side = st.side[slot]! === 1 ? 1 : 0;
  let corpse = -1;
  let pool = 0;
  for (let i = 0; i < st.n; i++) {
    if (isAlive(st, i) || st.side[i] !== side) continue;
    if ((st.flags[i]! & (F_UNDEAD | F_SUMMONED | F_WAR_MACHINE)) !== 0) continue;
    if (hasModFrom(c.s, i, SRC_CORPSE_RAISED as string)) continue;
    const hp = startCount(c.s, i) * st.maxHp[i]!;
    if (hp > pool) { pool = hp; corpse = i; }
  }
  if (corpse < 0) return;
  const count = Math.min(startCount(c.s, corpse), Math.trunc(pool / (demon.hp < 1 ? 1 : demon.hp)));
  if (count <= 0) return;
  const twoHex = (flagsForCreature(demon) & F_TWO_HEX) !== 0;
  const at = freeHexFor(c.s, side, st.hex[slot]!, twoHex);
  if (at < 0) return;
  const made = addStack(c, content, { def: demon.id, side, count, hex: at });
  if (made < 0) return;
  const spent = { stat: STAT_SUMMONER, op: "add" as const, value: 1, expires: { kind: "battle" as const } };
  addMod(c, slot, { source: SRC_SUMMONER_SPENT, ...spent });
  addMod(c, corpse, { source: SRC_CORPSE_RAISED, ...spent });
  c.events.push({ type: "BattleStackSummoned", stack: made, creature: demon.id, count });
}

/** Open a stack's turn. Returns false when the stack loses it. */
function beginTurn(c: Ctx, content: ContentRegistry, slot: number): boolean {
  const s = c.s;
  const reopened = s.waited[slot] === 1;
  setFlags(c, slot, F_TURN_STARTED);
  /**
   * Regeneration, before anything can take the turn away.
   *
   * "At the start of each of its turns" is the whole point of the spell over a
   * round-boundary heal: a stack that acts late in the round is mended late,
   * and a stack that loses the turn to Blind is still mended - the turn started,
   * it simply did nothing with it.
   */
  if (!reopened) {
    let heal = 0;
    for (const m of s.stacks.mods[slot]!) {
      if (m.stat === STAT_REGEN && m.value > heal) heal = m.value;
    }
    if (heal > 0) healStack(c, slot, heal, s.stacks.count[slot]!);
    repairMachines(c, content, slot);
    summonDemons(c, content, slot);
  }
  if ((s.stacks.flags[slot]! & F_DEFENDING) !== 0) {
    clearFlags(c, slot, F_DEFENDING);
    removeModsBySource(c, slot, SRC_DEFEND);
  }
  // The Runes attack latch is per turn, not per battle.
  removeModsBySource(c, slot, SRC_RUNE_TURN as string);
  // "The meditation state ends at the beginning of the Sun Serpent's next turn."
  if ((s.stacks.flags2[slot]! & F2_MEDITATING) !== 0) clearFlags2(c, slot, F2_MEDITATING);
  /**
   * The branches let go when there is nothing left holding on.
   *
   * Asked of the board rather than of a remembered binder: `F2_BOUND` does not
   * record WHO bound the stack, because that would be a column on `StackStore`
   * and `StackStore` is the save format. "Is a living enemy binder standing
   * next to me" has the same answer - a bound stack cannot walk away, so the
   * only ways for the binder to stop being adjacent are to die or to be moved
   * by something else, and both of those should release it.
   */
  if ((s.stacks.flags2[slot]! & F2_BOUND) !== 0 && !heldBy(s, content, slot)) {
    clearFlags2(c, slot, F2_BOUND);
  }
  // Blind and petrification cost the stack its turn, then lapse. Their real
  // durations belong to the spell that applied them, not to this file. The
  // stone's half-damage goes with the stone: a petrified stack that has woken
  // is an ordinary stack, not a hardened one for the rest of the battle.
  if ((s.stacks.flags[slot]! & (F_BLINDED | F_PETRIFIED)) !== 0) {
    if ((s.stacks.flags[slot]! & F_PETRIFIED) !== 0) removeModsBySource(c, slot, SRC_PETRIFY as string);
    // The spell's blindness holds until the stack is struck or the spell runs
    // out (`STAT_BLIND` stands); an ability's is the one lost turn.
    const held = (s.stacks.flags[slot]! & F_BLINDED) !== 0 && statusStands(s.stacks, slot, STAT_BLIND);
    clearFlags(c, slot, held ? F_PETRIFIED : F_BLINDED | F_PETRIFIED);
    return false;
  }
  // A stack that waited has had its turn opened once already this round:
  // Regeneration paid, Defend cleared, and the bad-morale die thrown. Rolling
  // again here would make a -3 stack that waits lose its turn 44% of the time
  // instead of 25%, so the second opening skips the roll.
  if (reopened) {
    c.events.push({ type: "BattleStackTurn", stack: slot });
    return true;
  }
  if (rollBadMorale(c, content, slot)) {
    c.events.push({ type: "BattleMorale", stack: slot, good: false });
    return false;
  }
  c.events.push({ type: "BattleStackTurn", stack: slot });
  return true;
}

/** Is a living enemy with Binding standing next to this stack? */
function heldBy(s: BattleState, content: ContentRegistry, slot: number): boolean {
  const st = s.stacks;
  for (let i = 0; i < st.n; i++) {
    if (i === slot || !isAlive(st, i) || st.side[i] === st.side[slot]) continue;
    if (!hasAbility(content, st.def[i]!, "BINDING")) continue;
    if (stacksAdjacent(st, i, slot)) return true;
  }
  return false;
}

// ------------------------------------------------------------- war machines

/**
 * Section 27 - a machine with no crew fires by itself.
 *
 * The classic only hands a war machine to the player when he has the skill for it:
 * Ballistics aims the catapult, Artillery the ballista, First Aid the tent.
 * With no skill the crew acts on its own, and this kernel has none of those
 * skills yet, so every machine is uncrewed and `settle` resolves its turn here
 * rather than stopping on a stack no screen has a control for. That is also
 * what keeps the barred gate honest: the gate comes down whether or not the
 * player finds a button, so an assault that can be started can be finished.
 */

/**
 * What the engine aims at: the gate, then the curtain wall, then the turrets.
 *
 * The classic aims at the wall and leaves the gate for last, and that ordering is wrong
 * for this battlefield rather than for the classic. The ditch here is cut in front of
 * every wall segment and skips only the gate row, so a breach in the curtain
 * puts the assault into a moat hex, which ends the move and costs ninety hit
 * points; the gateway is the one lane with no ditch across it. Measured with
 * the shipped AI on a Castle, wall-first turned a siege the attacker carried in
 * eight rounds into a hundred-round draw, because nobody would wade the ditch.
 * The engine therefore takes the drawbridge down first and the assault goes in
 * through the funnel the fortification was designed around.
 */
function batterTarget(s: BattleState): number {
  let best = -1;
  let bestRank = 3;
  for (let i = 0; i < s.obstacles.length; i++) {
    const o = s.obstacles[i]!;
    if (o.integrity <= 0) continue;
    const rank = o.kind === "gate" ? 0 : o.kind === "wall" ? 1 : o.kind === "tower" ? 2 : 3;
    if (rank < bestRank) { bestRank = rank; best = i; }
  }
  return best;
}

/** The fattest living enemy stack, ties to the lower slot. No roll is spent. */
function artilleryTarget(s: BattleState, slot: number): number {
  const st = s.stacks;
  let best = -1;
  let bestPool = -1;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] === st.side[slot]) continue;
    if (isUntargetable(st, i)) continue;
    const pool = (st.count[i]! - 1) * st.maxHp[i]! + st.topHp[i]!;
    if (pool > bestPool) { bestPool = pool; best = i; }
  }
  return best;
}

/** The friendly stack whose top creature is worst hurt, ties to the lower slot. */
function medicTarget(s: BattleState, slot: number): number {
  const st = s.stacks;
  let best = -1;
  let worst = 0;
  for (let i = 0; i < st.n; i++) {
    if (!isAlive(st, i) || st.side[i] !== st.side[slot]) continue;
    const missing = st.maxHp[i]! - st.topHp[i]!;
    if (missing > worst) { worst = missing; best = i; }
  }
  return best;
}

function batter(c: Ctx, seg: number, bite = 1): void {
  const s = c.s;
  const o = s.obstacles[seg]!;
  const integrity = o.integrity - bite < 0 ? 0 : o.integrity - bite;
  jSetField(c.j, o as unknown as object, "integrity", integrity);
  c.events.push({ type: "BattleWallHit", segment: seg, integrity });
  if (o.kind === "gate" && integrity <= 0 && s.siege !== null) {
    jSetField(c.j, s.siege as unknown as object, "gateOpen", true);
  }
}

/**
 * One volley. Ballistics buys reach as well as force: the crew keeps working
 * the aimed segment while it stands and moves to the next worth hitting once it
 * does not, so an expert crew never spends half a volley on rubble.
 */
function batterVolley(c: Ctx, seg: number, level: number): void {
  const hits = BALLISTICS_HITS[level] ?? 1;
  const bite = BALLISTICS_BITE[level] ?? 1;
  let at = seg;
  for (let n = 0; n < hits; n++) {
    if (at < 0) return;
    const o = c.s.obstacles[at];
    if (o === undefined || o.integrity <= 0) { at = batterTarget(c.s); continue; }
    batter(c, at, bite);
  }
}

/** What a tent mends in one turn: the crew's training, plus the hero's power. */
function tentHeal(s: BattleState, slot: number): number {
  const power = s.heroes[s.stacks.side[slot]!]!.spellPower;
  return (FIRST_AID_HEAL[crewLevel(s.stacks, slot)] ?? TENT_HEAL) + power * 5;
}

/** Section 27: a standing ammo cart pays for its side's shots. */
function spendShot(c: Ctx, slot: number): void {
  const st = c.s.stacks;
  if (sideHasAmmoCart(st, st.side[slot]!)) return;
  jSetI32(c.j, st.shots, slot, st.shots[slot]! - 1);
}

function autoMachineTurn(c: Ctx, content: ContentRegistry, slot: number): void {
  const s = c.s;
  const st = s.stacks;
  // A gun shoots. Battering is tested second so that a machine which can do
  // both - the Cannon - spends an unattended turn on the army and only turns on
  // the wall when it cannot fire, which is the order its own description
  // implies ("batters walls AS WELL AS troops"). The catapult has no bow, so
  // `canShoot` is false for it and this costs it nothing.
  if (canShoot(st, slot) && st.shots[slot]! > 0 && !meleeBlocked(s, slot)) {
    const target = artilleryTarget(s, slot);
    if (target >= 0) {
      spendShot(c, slot);
      resolveAttack(c, content, slot, target, true, 0, -1);
      return;
    }
  }
  if (canBatter(st, slot) && s.siege !== null && s.siege.active) {
    const seg = batterTarget(s);
    if (seg >= 0) batterVolley(c, seg, batterCrewLevel(st, slot));
    return;
  }
  if (canTend(st, slot)) {
    const target = medicTarget(s, slot);
    // The tent mends the top creature only: `resurrectTo` is the current count,
    // so a Field Hospital never raises the dead.
    if (target >= 0) healStack(c, target, tentHeal(s, slot), st.count[target]!);
  }
}

/**
 * A maddened stack's turn, taken for it.
 *
 * `F_BERSERK` was set by the Blood Rage spell, drawn as a status pip by the
 * renderer, and read by NO RULE - `spells.ts` said "the flag is what
 * `chooseBattleCommand` and the turn machine read" and neither of them
 * mentioned it. The spell charged mana, showed an icon and changed nothing.
 * This is the missing half.
 *
 * The rule is the classic's: the stack attacks whatever is nearest, friend or foe, and
 * its owner does not get to choose. Resolved here rather than offered as a
 * command for the same reason an uncrewed war machine is - there is nothing for
 * a player to decide, and a turn that stopped on a stack with no legal command
 * would stall the machine.
 *
 * Nearest by hex distance, ties to the lower slot, so it is deterministic
 * without spending a roll (I5). A stack that can reach nothing simply loses the
 * turn, which is what being out of your mind on the far side of the field looks
 * like.
 */
function autoBerserkTurn(c: Ctx, content: ContentRegistry, slot: number): void {
  const s = c.s;
  const st = s.stacks;
  const here = st.hex[slot]!;

  let best = -1;
  let bestDist = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < st.n; i++) {
    if (i === slot || !isAlive(st, i) || isUntargetable(st, i)) continue;
    const d = hexDistance(here, st.hex[i]!);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  if (best < 0) return;

  // Already in contact: swing without moving.
  if (stacksAdjacent(st, slot, best)) {
    resolveAttack(c, content, slot, best, false, 0, here);
    return;
  }

  const occ = newOccupancy(s);
  const reach = computeReach(s, slot, occ);
  let step = -1;
  let stepDist = Number.MAX_SAFE_INTEGER;
  for (let h = 0; h < BF_HEXES; h++) {
    if (reach.dist[h] === UNREACHABLE || h === here) continue;
    if (!wouldBeAdjacent(s, slot, h, best)) continue;
    const d = reach.dist[h]!;
    if (d < stepDist) { stepDist = d; step = h; }
  }
  if (step < 0) return;

  const path = pathTo(s, slot, step, reach);
  if (path === null) return;
  const travelled = reach.dist[step]!;
  moveStack(c, slot, step, path);
  applyHazardOnEnter(c, content, slot, step);
  if (!isAlive(st, slot)) return;
  resolveAttack(c, content, slot, best, false, travelled, here);
}

/** Walk forward until the active stack is one that can actually be commanded. */
function settle(c: Ctx, content: ContentRegistry): void {
  const s = c.s;
  for (let guard = 0; guard < 8192; guard++) {
    if (s.outcome.kind !== "ongoing") return;
    if (s.tacticsPhase) return;
    if (s.turnIndex >= s.order.length || s.order[s.turnIndex]! < 0) {
      startRound(c, content);
      if (s.outcome.kind !== "ongoing") return;
      continue;
    }
    const slot = s.order[s.turnIndex]!;
    if (!isAlive(s.stacks, slot)) { jSetField(c.j, s, "turnIndex", s.turnIndex + 1); continue; }
    if ((s.stacks.flags[slot]! & F_TURN_STARTED) === 0 && !beginTurn(c, content, slot)) {
      setFlags(c, slot, F_ACTED);
      jSetField(c.j, s, "turnIndex", s.turnIndex + 1);
      continue;
    }
    // A crewed machine is the player's to command, so the turn stops on it the
    // way it stops on a creature; an untrained one is resolved here.
    if (isWarMachine(s.stacks, slot) && !isCrewed(s.stacks, slot)) {
      autoMachineTurn(c, content, slot);
      setFlags(c, slot, F_ACTED);
      if (checkVictory(c)) return;
      jSetField(c.j, s, "turnIndex", s.turnIndex + 1);
      continue;
    }
    // A maddened stack is nobody's to command, so its turn is taken here.
    if ((s.stacks.flags[slot]! & F_BERSERK) !== 0) {
      autoBerserkTurn(c, content, slot);
      setFlags(c, slot, F_ACTED);
      if (checkVictory(c)) return;
      jSetField(c.j, s, "turnIndex", s.turnIndex + 1);
      continue;
    }
    return;
  }
}

/** Finish an action: victory, then the good-morale extra action, then advance. */
function endAction(c: Ctx, content: ContentRegistry, slot: number, moraleAllowed: boolean): void {
  const s = c.s;
  setFlags(c, slot, F_ACTED);
  if (checkVictory(c)) return;
  if (moraleAllowed && isAlive(s.stacks, slot) && (s.stacks.flags[slot]! & F_MORALE_USED) === 0
      && rollGoodMorale(c, content, slot)) {
    setFlags(c, slot, F_MORALE_USED);
    clearFlags(c, slot, F_ACTED | F_MOVED);
    c.events.push({ type: "BattleMorale", stack: slot, good: true });
    return;
  }
  jSetField(c.j, s, "turnIndex", s.turnIndex + 1);
  settle(c, content);
}

// ------------------------------------------------------------------ movement

function moveStack(
  c: Ctx, slot: number, target: number, path: readonly number[], attacking = -1,
): void {
  if (path.length === 0) return;
  const from = c.s.stacks.hex[slot]!;
  setHex(c, slot, target);
  setFlags(c, slot, F_MOVED);
  reorient(c, slot, from, target, attacking);
  c.events.push({ type: "BattleStackMoved", stack: slot, path: [...path] });
}

/**
 * Section 21 - a two-hex creature turns to face where it is going.
 *
 * A dragon that walks past a target and keeps facing the way it started is
 * standing on the wrong two hexes and swinging from the wrong one. The classic flips a
 * two-hex unit when it crosses the field, and the flip is not free: the tail
 * moves to the other side of the head, so it only happens when that hex is
 * clear. A stack that cannot turn simply does not - which is the same answer
 * the movement rules give everywhere else, and never a stack standing on a hex
 * somebody else is on.
 */
function reorient(c: Ctx, slot: number, from: number, to: number, attacking = -1): void {
  const st = c.s.stacks;
  if ((st.flags[slot]! & F_TWO_HEX) === 0) return;
  const dx = hexCol(to) - hexCol(from);
  if (dx === 0) return;
  const want = dx > 0 ? 1 : -1;
  if (st.facing[slot] === want) return;
  const t = tailHex(to, want);
  if (t < 0) return;
  const occ = newOccupancy(c.s);
  if (occ[t]! >= 0 && occ[t] !== slot) return;
  if (obstacleAt(c.s, t) !== null) return;
  // A stack that walked up to strike must still be touching its target once it
  // has turned. The attack was validated against the tail it arrived with; a
  // flip that swings the tail away from the target would leave the blow landing
  // from two hexes off and deny the retaliation, so that flip does not happen.
  if (attacking >= 0) {
    const theirs: number[] = [0, 0];
    const k = stackHexes(st, attacking, theirs);
    let touching = false;
    for (let m = 0; m < k && !touching; m++) {
      if (hexDistance(to, theirs[m]!) === 1 || hexDistance(t, theirs[m]!) === 1) touching = true;
    }
    if (!touching) return;
  }
  jSetI32(c.j, st.facing, slot, want);
}

/**
 * What a stack finds where it has just stopped: the ditch, a Flame Barrier, a Land
 * Mine, a patch of Sinking Sand. All four are "the ground here costs you
 * something", which is why one pass answers for all of them; what differs is
 * how much it costs and whether the ground is used up.
 */
/**
 * The ground a stack CROSSES, as opposed to the ground it stops on. A Fire
 * Wall burns everything that passes through it and is not used up; a Land
 * Mine goes off under the first foot and ends the move there, which is why
 * this returns the path actually walked - the caller stops where it says.
 * Both used to bite only the destination hex, so a wall was free to walk
 * through and a mine was safe to step over.
 */
function crossHazards(
  c: Ctx, content: ContentRegistry, slot: number, path: readonly number[],
): readonly number[] {
  for (let i = 0; i < path.length - 1; i++) {
    const hex = path[i]!;
    let burn = 0;
    let mined = false;
    for (const o of c.s.obstacles) {
      if (o.hex !== hex) continue;
      if (o.kind === "firewall") burn += o.power ?? 0;
      if (o.kind === "landmine") mined = true;
    }
    // The mine is applied by `applyHazardOnEnter` on the hex the move now ends
    // on, so it is spent exactly once and reported the same way as the moat.
    if (mined) return path.slice(0, i + 1);
    if (burn <= 0) continue;
    const r = damageStack(c, slot, burn);
    c.events.push({
      type: "BattleAttacked", attacker: slot, target: slot,
      damage: r.dealt, killed: r.killed, ranged: false, retaliation: false,
    });
    if (r.died) {
      onUnitDeathAbilities(c, content, slot, r.killed);
      rallyOnDeath(c, content, slot);
      return path.slice(0, i + 1);
    }
  }
  return path;
}

function applyHazardOnEnter(c: Ctx, content: ContentRegistry, slot: number, hex: number): void {
  let spent = false;
  const rest: Obstacle[] = [];
  let damage = 0;
  for (const o of c.s.obstacles) {
    if (o.hex !== hex) { rest.push(o); continue; }
    if (o.kind === "moat") { damage += MOAT_DAMAGE; rest.push(o); continue; }
    if (o.kind === "firewall") { damage += o.power ?? 0; rest.push(o); continue; }
    // Embers burn whoever stops in them and go on burning: the patch is ground,
    // not a mine, so it is never spent and never removed.
    if (o.kind === "embers") { rest.push(o); continue; }
    if (o.kind === "landmine") { damage += o.power ?? 0; spent = true; continue; }
    // Sinking Sand costs no blood: `endsMove` already stopped the stack here, and
    // the patch is used up doing it.
    if (o.kind === "sinking_sand") { spent = true; continue; }
    rest.push(o);
  }
  if (spent) jSetField(c.j, c.s as unknown as object, "obstacles", rest);
  damage += hazardBite(c.s, hex);
  if (damage <= 0) return;
  const r = damageStack(c, slot, damage);
  c.events.push({
    type: "BattleAttacked", attacker: slot, target: slot,
    damage: r.dealt, killed: r.killed, ranged: false, retaliation: false,
  });
  if (r.died) {
    onUnitDeathAbilities(c, content, slot, r.killed);
    rallyOnDeath(c, content, slot);
  }
}

// ---------------------------------------------------------------- retaliation

function retaliationBudget(c: Ctx, content: ContentRegistry, slot: number): number {
  if (hasAbility(content, c.s.stacks.def[slot]!, "UNLIMITED_RETALIATION")) return 1 << 20;
  const extra = abilityOf(content, c.s.stacks.def[slot]!, "EXTRA_RETALIATIONS");
  // Riposte buys the same thing the ability does, so it is added the same
  // way rather than through a second mechanism.
  let spell = 0;
  for (const m of c.s.stacks.mods[slot]!) if (m.stat === STAT_EXTRA_RETALIATION) spell += m.value;
  return 1 + (extra === null ? 0 : extra.count) + (spell > 0 ? spell : 0);
}

function canRetaliate(c: Ctx, content: ContentRegistry, defender: number, attacker: number): boolean {
  const st = c.s.stacks;
  if (!isAlive(st, defender)) return false;
  if ((st.flags[attacker]! & F_NO_RETALIATION) !== 0) return false;
  if ((st.flags[defender]! & (F_BLINDED | F_PETRIFIED | F_WAR_MACHINE)) !== 0) return false;
  if ((st.flags[defender]! & F_RETALIATED) !== 0) return false;
  if (retaliationsUsed(st, defender) >= retaliationBudget(c, content, defender)) return false;
  return stacksAdjacent(st, defender, attacker);
}

function spendRetaliation(c: Ctx, content: ContentRegistry, defender: number): void {
  const st = c.s.stacks;
  // The counter is two bits wide and saturates at 3; the budget can be 4
  // (expert Riposte on a creature with an extra retaliation). Decide
  // "spent" on the true count, before the clamp, or such a stack never stops.
  const spent = retaliationsUsed(st, defender) + 1;
  const used = Math.min(3, spent);
  const f = (st.flags[defender]! & ~F_RETAL_MASK) | (used << F_RETAL_SHIFT);
  jSetI32(c.j, st.flags, defender, f);
  if (spent >= retaliationBudget(c, content, defender)) setFlags(c, defender, F_RETALIATED);
}

// ---------------------------------------------------------------- one blow

/**
 * The shot a stack gets off at whoever is taking aim at it.
 *
 * `perRound` of 0 means no limit, which is how the Bounty Pistoleer is written -
 * The expansion gives it unlimited preemptive shots and the Pistoleer one a round.
 * Costs a shot like any other, so a Pistoleer out of ammo cannot preempt.
 */
function preemptiveShot(
  c: Ctx, content: ContentRegistry, defender: number, shooter: number,
): void {
  const st = c.s.stacks;
  if (!isAlive(st, defender) || !isAlive(st, shooter)) return;
  if (st.side[defender] === st.side[shooter]) return;
  let perRound = -1;
  for (const a of abilitiesOf(content, st.def[defender]!)) {
    if (a.kind === "PREEMPTIVE_SHOT") { perRound = a.perRound; break; }
  }
  if (perRound < 0) return;
  if (perRound > 0 && (st.flags2[defender]! & F2_PREEMPTED) !== 0) return;
  if (st.shots[defender]! <= 0) return;
  if (perRound > 0) setFlags2(c, defender, F2_PREEMPTED);
  jSetI32(c.j, st.shots, defender, st.shots[defender]! - 1);
  strike(c, content, defender, shooter, true, 0, false);
}

type MagicAttack = Extract<CreatureAbility, { kind: "MAGIC_ATTACK" }>;

/** The charged-blow ability a creature carries, or null. */
function magicAttackAbility(content: ContentRegistry, def: CreatureDefId): MagicAttack | null {
  for (const a of must(content.creatures, def, "creature").abilities) {
    if (a.kind === "MAGIC_ATTACK") return a;
  }
  return null;
}

function strike(
  c: Ctx, content: ContentRegistry, attacker: number, target: number,
  ranged: boolean, hexesTravelled: number, retaliation: boolean,
  magic: MagicAttack | null = null,
): void {
  const st = c.s.stacks;
  if (!isAlive(st, attacker) || !isAlive(st, target)) return;

  const luck = rollLuck(c, attacker);
  if (luck !== 0) c.events.push({ type: "BattleLuck", stack: attacker, good: luck > 0 });

  const base = rollBaseDamage(c.s, c.j, attacker);
  const canopy = ranged && canopyHalves(c.s, content, target);
  const boardBp = ranged ? 0 : boardingIgnoreBp(c.s, content, attacker);
  const explained = computeDamage(c.s, content, {
    attacker, defender: target, baseDamage: base, ranged, luck, hexesTravelled,
    // The classic halves a shot fired OVER the wall into the town; the besieged town's
    // own archers shoot out at full strength. The geometry test is symmetric,
    // so the side is asked here.
    wallObstructed: ranged && st.side[attacker] === SIDE_ATTACKER
      && shotCrossesWall(c.s, st.hex[attacker]!, st.hex[target]!),
    retaliation,
    attackDelta: miasmaPenalty(c.s, content, attacker) ? -1 : 0,
    defenseDelta: miasmaPenalty(c.s, content, target) ? -1 : 0,
    defIgnoreBp: boardBp,
    extraBase: cindersBonus(c.s, content, attacker) ? 1 : 0,
    half: canopy,
    cover: ranged && inHardCover(c.s, target),
    // Asked on EVERY blow, not only a charged one: an enchanted weapon is a
    // modifier on the attacker and rides every swing it makes, where the
    // charge is paid for per action. `magicStrikeFp` returns FP_ONE when the
    // stack carries neither, so an ordinary creature adds no term at all.
    //
    // A retaliation passes `magic: null` - the budget bought the attacker's
    // blows, not the defender's reply - but an enchantment on the DEFENDER
    // still counts, because the spell was cast on the stack and not on a turn.
    ...(() => {
      const fp = magicStrikeFp(c.s, content, attacker, target, retaliation ? null : magic);
      return fp === FP_ONE ? {} : { magicFp: fp };
    })(),
  });

  const res = damageStack(c, target, explained.value);
  creditKills(c, attacker, res.killed);
  if (canopy) {
    addMod(c, target, {
      source: SRC_CANOPY, stat: "defense", op: "add", value: 0,
      expires: { kind: "round", rounds: 1 },
    });
  }
  if (boardBp > 0) {
    addMod(c, attacker, {
      source: SRC_BOARDING, stat: "attack", op: "add", value: 0,
      expires: { kind: "battle" },
    });
  }
  onPerkKill(c, content, attacker, target, res.killed);
  // Runes. The attacker earns one level per TURN however many blows it lands,
  // so the latch is checked here rather than counting strikes; the defender
  // earns two for being hit, every time, retaliation included.
  if (!retaliation && !hasModFrom(c.s, attacker, SRC_RUNE_TURN)) {
    addMod(c, attacker, {
      source: SRC_RUNE_TURN, stat: "attack", op: "add", value: 0,
      expires: { kind: "battle" },
    });
    gainRunes(c, content, attacker, 1);
  }
  if (res.dealt > 0) gainRunes(c, content, target, 2);
  c.events.push({
    type: "BattleAttacked", attacker, target,
    damage: res.dealt, killed: res.killed, ranged, retaliation,
  });
  if (!retaliation) wakeOnHit(c, target);
  // Brine Hexer: having shot, lay a spell on what was shot. After the damage and
  // before the on-hit abilities, so a stack killed by the shot is not cursed
  // as well - `castAfterShot` checks `isAlive` for exactly that.
  if (ranged) castAfterShot(c, content, attacker, target);
  onDamageAbilities(c, content, attacker, target, res, ranged);
  if (res.died) {
    onUnitDeathAbilities(c, content, target, res.killed);
    rallyOnDeath(c, content, target);
  }
}

/**
 * One whole attack action: the blow, the defender's single retaliation, then any
 * second strike. Splash victims (attack-all-around, breath) take the blow with
 * their own luck roll - The expansion applies luck per target - and never retaliate.
 */
function resolveAttack(
  c: Ctx, content: ContentRegistry, attacker: number, target: number,
  ranged: boolean, hexesTravelled: number, returnHex: number,
  magic: MagicAttack | null = null,
): void {
  const st = c.s.stacks;
  // Preemptive shot: a Pistoleer being shot at fires FIRST, and the shot that
  // provoked it may never arrive - `strike` below is a no-op once its attacker
  // is dead. That ordering is the whole ability, so it happens before anything
  // else in the attack.
  if (ranged) preemptiveShot(c, content, target, attacker);
  if (!isAlive(st, attacker) || !isAlive(st, target)) return;
  const shape = shapeAttack(c.s, content, attacker, target, ranged);

  // An expert Blind's waking blow draws no retaliation. Read before the blow,
  // because the blow is what wakes the stack and clears the note.
  const blindLevel = blindMastery(st, target);
  let ferocityUsed = false;
  for (let n = 0; n < shape.strikes; n++) {
    if (!isAlive(st, attacker) || !isAlive(st, target)) break;
    const countBefore = st.count[target]!;
    strike(c, content, attacker, target, ranged, n === 0 ? hexesTravelled : 0, false, magic);
    const killedThis = countBefore - st.count[target]!;
    for (const extra of shape.splash) {
      if (!isAlive(st, attacker)) break;
      strike(c, content, attacker, extra, ranged, 0, false, magic);
    }
    if (n === 0 && !ranged && blindLevel < 4 && canRetaliate(c, content, target, attacker)) {
      spendRetaliation(c, content, target);
      // Not charged: this is the DEFENDER swinging, out of nobody's budget.
      strike(c, content, target, attacker, false, 0, true);
    }
    // Ferocity: a blow that killed something earns another, once. Checked on
    // `killedThis` rather than on the target still being alive, because the expansion's
    // condition is "at least one creature was killed by the attack" - wiping
    // the stack out entirely still earned the blow, it simply has nowhere to
    // land, which the `isAlive` guard inside `strike` handles.
    if (n === 0 && killedThis > 0 && !ferocityUsed
      && hasAbility(content, st.def[attacker]!, "FEROCITY")) {
      ferocityUsed = true;
      strike(c, content, attacker, target, ranged, 0, false, magic);
    }
    // Banked meals, spent one at a time and only after the retaliation - The expansion:
    // "if the target is still alive it retaliates, and then bonus strikes
    // start". A stack that ate four corpses gets four, which is the point.
    if (n === shape.strikes - 1) {
      while (isAlive(st, attacker) && isAlive(st, target)
        && spendBankedStrike(c, attacker)) {
        strike(c, content, attacker, target, ranged, 0, false, magic);
      }
    }
  }

  if (shape.returns && returnHex >= 0 && isAlive(st, attacker)) {
    const occ = newOccupancy(c.s);
    if (occ[returnHex]! < 0 || occ[returnHex] === attacker) {
      setHex(c, attacker, returnHex);
      c.events.push({ type: "BattleStackMoved", stack: attacker, path: [returnHex] });
    }
  }
}

// ------------------------------------------------------------------ validation

function structural(cmd: BattleCommand, s: BattleState): string[] {
  const issues: string[] = [];
  const n = s.stacks.n;
  const chkStack = (v: number, name: string): void => {
    if (!Number.isInteger(v) || v < 0 || v >= n) issues.push(`${name} out of range: ${String(v)}`);
  };
  const chkHex = (v: number, name: string): void => {
    if (!Number.isInteger(v) || v < 0 || v >= BF_HEXES) issues.push(`${name} out of range: ${String(v)}`);
  };
  switch (cmd.type) {
    case "BATTLE_MOVE": chkStack(cmd.stack, "stack"); chkHex(cmd.hex, "hex"); break;
    case "BATTLE_ATTACK": chkStack(cmd.stack, "stack"); chkStack(cmd.target, "target"); chkHex(cmd.hex, "hex"); break;
    case "BATTLE_SHOOT": chkStack(cmd.stack, "stack"); chkStack(cmd.target, "target"); break;
    case "BATTLE_WAIT": case "BATTLE_DEFEND": case "BATTLE_MEDITATE":
      chkStack(cmd.stack, "stack"); break;
    case "BATTLE_DEVOUR": chkStack(cmd.stack, "stack"); chkStack(cmd.target, "target"); break;
    case "BATTLE_HEAT_STROKE": chkStack(cmd.stack, "stack"); break;
    case "BATTLE_HEAL": chkStack(cmd.stack, "stack"); chkStack(cmd.target, "target"); break;
    case "BATTLE_CATAPULT": chkStack(cmd.stack, "stack"); break;
    case "BATTLE_CREATURE_CAST": chkStack(cmd.stack, "stack"); break;
    case "BATTLE_ACTIVATE": chkStack(cmd.stack, "stack"); break;
    case "BATTLE_HERO_STRIKE":
      chkStack(cmd.target, "target");
      if (cmd.side !== 0 && cmd.side !== 1) issues.push(`side out of range: ${String(cmd.side)}`);
      break;
    case "BATTLE_RACIAL":
      chkStack(cmd.stack, "stack");
      if (cmd.side !== 0 && cmd.side !== 1) issues.push(`side out of range: ${String(cmd.side)}`);
      break;
    case "BATTLE_TACTICS_MOVE": chkStack(cmd.stack, "stack"); chkHex(cmd.hex, "hex"); break;
    case "BATTLE_CAST": case "BATTLE_TACTICS_DONE": case "BATTLE_RETREAT": case "BATTLE_SURRENDER":
      if (cmd.side !== 0 && cmd.side !== 1) issues.push(`side out of range: ${String(cmd.side)}`);
      break;
  }
  return issues;
}

// -------------------------------------------------------------------- execute

export function executeBattleCommand(
  state: BattleState, command: BattleCommand, content: ContentRegistry,
  opts?: BattleExecuteOpts,
): Result<CommandResult<BattleState>, ExecuteError> {
  const issues = structural(command, state);
  if (issues.length > 0) return Err(malformed(...issues));

  const j: BattleJournal = getJournal(state);
  const mark: JournalMark = forkBattle(state);
  const events: GameEvent[] = [];
  const c: Ctx = { s: state, j, events };

  const fail = (e: ExecuteError): Result<never, ExecuteError> => {
    undoTo(state, mark);
    return Err(e);
  };

  if (state.outcome.kind !== "ongoing") return fail(illegal({ code: "NO_BATTLE_IN_PROGRESS" }));

  const r = dispatch(c, command, content, fail);
  if (!r.ok) return r;

  if (opts?.verify === true) {
    return Ok({ state, events, hash: hashState(state) });
  }
  return Ok({ state, events });
}

type Fail = (e: ExecuteError) => Result<never, ExecuteError>;

function dispatch(
  c: Ctx, cmd: BattleCommand, content: ContentRegistry, fail: Fail,
): Result<true, ExecuteError> {
  const s = c.s;
  const st = s.stacks;

  switch (cmd.type) {
    // ---------------------------------------------------------------- tactics
    case "BATTLE_TACTICS_MOVE": {
      if (!s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));
      if (!isAlive(st, cmd.stack)) return fail(illegal({ code: "STACK_DEAD", stack: cmd.stack }));
      const occ = newOccupancy(s);
      if (occ[cmd.hex]! >= 0 && occ[cmd.hex] !== cmd.stack) {
        return fail(illegal({ code: "HEX_OCCUPIED", hex: cmd.hex }));
      }
      // The same test the enumerator applies: a rock, a tree, or a two-hex
      // tail hanging off the field is no place to stand (3.3).
      if (!canOccupy(s, occ, cmd.stack, cmd.hex)) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      const side = st.side[cmd.stack]!;
      if (!inTacticsBand(s, side, hexCol(cmd.hex))) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      setHex(c, cmd.stack, cmd.hex);
      c.events.push({ type: "BattleStackMoved", stack: cmd.stack, path: [cmd.hex] });
      return Ok(true);
    }
    case "BATTLE_TACTICS_DONE": {
      if (!s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));
      if (cmd.side !== tacticsSide(s)) return fail(illegal({ code: "NOT_YOUR_TURN" }));
      jSetField(c.j, s, "tacticsPhase", false);
      settle(c, content);
      return Ok(true);
    }

    // ------------------------------------------------------------- concession
    case "BATTLE_RETREAT": {
      if (s.heroes[cmd.side].id === null) return fail(illegal({ code: "TARGET_INVALID" }));
      if (escapeBarred(s)) return fail(illegal({ code: "TARGET_INVALID" }));
      jSetField(c.j, s, "outcome", { kind: "retreat", side: cmd.side } as BattleState["outcome"]);
      c.events.push({ type: "BattleEnded", winner: (1 - cmd.side) as 0 | 1, kind: "retreat" });
      return Ok(true);
    }
    case "BATTLE_SURRENDER": {
      if (escapeBarred(s)) return fail(illegal({ code: "TARGET_INVALID" }));
      if (s.heroes[cmd.side].id === null) return fail(illegal({ code: "TARGET_INVALID" }));
      if (s.heroes[1 - cmd.side]!.id === null) return fail(illegal({ code: "TARGET_INVALID" }));
      jSetField(c.j, s, "outcome", { kind: "surrender", side: cmd.side } as BattleState["outcome"]);
      c.events.push({ type: "BattleEnded", winner: (1 - cmd.side) as 0 | 1, kind: "surrender" });
      return Ok(true);
    }

    // ------------------------------------------------------------------ spells
    //
    // A hero's spell is cast during one of its own stacks' turns and costs that
    // stack its action (Section 52), so the cast lands here - before the active
    // -stack gate, because the command names a side, not a stack - and then ends
    // the active stack's turn itself. `castHeroSpell` does the gate.
    case "BATTLE_CAST": {
      if (s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));
      const r = castHeroSpell(c, content, cmd.side, cmd.spell, cmd.target);
      if (!r.ok) return fail(illegal(r.error));
      const caster = r.value;
      if (caster >= 0 && !checkVictory(c)) endAction(c, content, caster, false);
      return Ok(true);
    }

    /**
     * The hero swings. Side-addressed and priced exactly like a cast: it takes
     * the round's hero action and ends the active stack's turn, so it fills an
     * empty round rather than adding an action to a full one.
     */
    case "BATTLE_HERO_STRIKE": {
      if (s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));
      const r = heroStrike(c, cmd.side, cmd.target);
      if (!r.ok) return fail(illegal(r.error));
      c.events.push({
        type: "BattleHeroStruck", side: cmd.side, target: cmd.target,
        damage: r.value.dealt, killed: r.value.killed,
      });
      /**
       * The hero was carried off answering for it.
       *
       * Reported as a `retreat` by the side that lost him, which is not a
       * euphemism: it is the outcome that already means "the hero lives, the
       * army does not", and every consequence of one - the hero going back to
       * the tavern pool, the army being written off, the winner taking the
       * ground - is written against it. A knockout is a retreat the enemy
       * chose for you.
       *
       * Ahead of `checkVictory` and ahead of ending the turn: the fight is
       * over, so there is no turn left to end.
       */
      if (r.value.downed) {
        jSetField(c.j, s, "outcome", { kind: "retreat", side: cmd.side } as BattleState["outcome"]);
        c.events.push({ type: "BattleEnded", winner: (1 - cmd.side) as 0 | 1, kind: "retreat" });
        return Ok(true);
      }
      const caster = r.value.caster;
      if (caster >= 0 && !checkVictory(c)) endAction(c, content, caster, false);
      return Ok(true);
    }

    /**
     * The faction racial. Side-addressed for the same reason the cast is - the
     * charge belongs to the hero - and it names the stack it is spent on, which
     * `validateRacial` insists is the active one.
     */
    case "BATTLE_RACIAL": {
      if (s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));
      const kind = racialOf(s, content, cmd.side)?.kind ?? "";
      const r = spendRacial(c, content, cmd.side, cmd.stack);
      if (!r.ok) return fail(illegal(r.error));
      c.events.push({ type: "BattleRacialUsed", side: cmd.side, stack: cmd.stack, kind });
      const spent = r.value;
      if (spent >= 0 && !checkVictory(c)) endAction(c, content, spent, false);
      return Ok(true);
    }

    default: break;
  }

  if (s.tacticsPhase) return fail(illegal({ code: "NOT_IN_TACTICS_PHASE" }));

  const active = activeStack(s);
  const slot = cmd.stack;
  if (slot !== active) return fail(illegal({ code: "STACK_NOT_ACTIVE", stack: slot }));
  if (!isAlive(st, slot)) return fail(illegal({ code: "STACK_DEAD", stack: slot }));
  if ((st.flags[slot]! & F_ACTED) !== 0) return fail(illegal({ code: "STACK_NOT_ACTIVE", stack: slot }));

  switch (cmd.type) {
    // ------------------------------------------------------- activated ability
    case "BATTLE_ACTIVATE": {
      const r = activate(c, content, slot);
      if (!r.ok) return fail(illegal(r.error));
      // Never the good-morale extra action: an ability that could roll a second
      // turn would be an ability whose cooldown starts a round early.
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // ------------------------------------------------------------- creature cast
    case "BATTLE_CREATURE_CAST": {
      const r = castCreatureSpell(c, content, slot, cmd.spell, cmd.target);
      if (!r.ok) return fail(illegal(r.error));
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // -------------------------------------------------------------------- wait
    case "BATTLE_WAIT": {
      if ((st.flags[slot]! & F_WAITED) !== 0) return fail(illegal({ code: "STACK_NOT_ACTIVE", stack: slot }));
      setFlags(c, slot, F_WAITED);
      // The turn is re-opened when the wait queue reaches this stack, so a
      // Blind or petrification landed in the meantime still costs the turn.
      // `beginTurn` reads `s.waited` to know it is the second opening.
      clearFlags(c, slot, F_TURN_STARTED);
      jSetI32(c.j, s.waited, slot, 1);
      reorderForWait(s, c.j);
      settle(c, content);
      return Ok(true);
    }

    // ----------------------------------------------------------- heat stroke
    case "BATTLE_HEAT_STROKE": {
      if (!hasAbility(content, st.def[slot]!, "HEAT_STROKE")) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      const hexes: number[] = [];
      const k = heatStrokeHexes(st.hex[slot]!, st.facing[slot]!, hexes);
      const seen: number[] = [];
      for (let i = 0; i < k; i++) {
        for (let v = 0; v < st.n; v++) {
          if (v === slot || !isAlive(st, v)) continue;
          if (seen.includes(v)) continue;
          // Two-hex stacks count if EITHER hex is in the fan.
          const own: number[] = [];
          const oc = stackHexes(st, v, own);
          let touched = false;
          for (let m = 0; m < oc; m++) if (own[m] === hexes[i]) { touched = true; break; }
          if (!touched) continue;
          seen.push(v);
          // Friend and foe alike - that is the cost of the ability - and no
          // retaliation, so `strike` is called with the retaliation flag set
          // to keep the defender from swinging back.
          strike(c, content, slot, v, false, 0, true);
        }
      }
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // --------------------------------------------------------------- devour
    case "BATTLE_DEVOUR": {
      if (!canDevour(content, c.s, slot, cmd.target)) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      devour(c, slot, cmd.target);
      c.events.push({ type: "CorpseDevoured", eater: slot, corpse: cmd.target });
      // Eating IS the action - The expansion spends the attack or the move on it.
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // -------------------------------------------------------------- meditate
    case "BATTLE_MEDITATE": {
      if (!hasAbility(content, st.def[slot]!, "MEDITATION")) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      // Once a battle. `F_MEDITATED` is not round-scoped, so the latch holds
      // for the whole fight rather than resetting with the round flags.
      if ((st.flags2[slot]! & F2_MEDITATED) !== 0) {
        return fail(illegal({ code: "STACK_NOT_ACTIVE", stack: slot }));
      }
      setFlags2(c, slot, F2_MEDITATING | F2_MEDITATED);
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // ------------------------------------------------------------------ defend
    case "BATTLE_DEFEND": {
      setFlags(c, slot, F_DEFENDING);
      addMod(c, slot, defendModifier());
      // The most rune levels any single action is worth - which is why a
      // Frostmark line that stands still gets stronger while it waits.
      gainRunes(c, content, slot, 3);
      // Defending never triggers the good-morale extra action in the classic.
      endAction(c, content, slot, false);
      return Ok(true);
    }

    // -------------------------------------------------------------------- move
    case "BATTLE_MOVE": {
      if (isWarMachine(st, slot)) return fail(illegal({ code: "TARGET_INVALID" }));
      const occ = newOccupancy(s);
      const reach = computeReach(s, slot, occ);
      if (reach.dist[cmd.hex] === UNREACHABLE) {
        return fail(occ[cmd.hex]! >= 0
          ? illegal({ code: "HEX_OCCUPIED", hex: cmd.hex })
          : illegal({ code: "PATH_BLOCKED", atIndex: 0 }));
      }
      const path = pathTo(s, slot, cmd.hex, reach);
      if (path === null) return fail(illegal({ code: "PATH_BLOCKED", atIndex: 0 }));
      const walked = crossHazards(c, content, slot, path);
      const stopped = walked[walked.length - 1]!;
      moveStack(c, slot, stopped, walked);
      applyHazardOnEnter(c, content, slot, stopped);
      endAction(c, content, slot, true);
      return Ok(true);
    }

    // ------------------------------------------------------------------ attack
    case "BATTLE_ATTACK": {
      const target = cmd.target;
      if (isWarMachine(st, slot)) return fail(illegal({ code: "TARGET_INVALID" }));
      // Fear. Refused here as well as unenumerated, because the enumerator is a
      // convenience and this is the gate (3.3).
      if ((st.flags2[slot]! & F2_AFRAID) !== 0) return fail(illegal({ code: "TARGET_INVALID" }));
      if (!isAlive(st, target)) return fail(illegal({ code: "STACK_DEAD", stack: target }));
      if (st.side[target] === st.side[slot]) return fail(illegal({ code: "TARGET_INVALID" }));
      if (isUntargetable(st, target)) return fail(illegal({ code: "TARGET_INVALID" }));

      // The charge is decided and PAID before the walk, so a stack that dies
      // to a mine on the way has still spent the use it committed. Refusing
      // here rather than silently swinging plain is the point: the player
      // asked for the expensive blow and is owed an answer either way.
      const magic = cmd.magic === true ? magicAttackAbility(content, st.def[slot]!) : null;
      if (cmd.magic === true) {
        if (magic === null) return fail(illegal({ code: "TARGET_INVALID" }));
        if (st.strikes[slot]! <= 0) return fail(illegal({ code: "NO_SHOTS_LEFT" }));
        if (st.cooldown[slot]! > 0) return fail(illegal({ code: "NO_SHOTS_LEFT" }));
        jSetI32(c.j, st.strikes, slot, st.strikes[slot]! - 1);
        const cool = magic.cooldownRounds ?? 0;
        if (cool > 0) jSetI32(c.j, st.cooldown, slot, cool);
      }

      const from = st.hex[slot]!;
      let travelled = 0;
      if (cmd.hex !== from) {
        const occ = newOccupancy(s);
        const reach = computeReach(s, slot, occ);
        if (reach.dist[cmd.hex] === UNREACHABLE) return fail(illegal({ code: "PATH_BLOCKED", atIndex: 0 }));
        if (!wouldBeAdjacent(s, slot, cmd.hex, target)) return fail(illegal({ code: "NOT_ADJACENT" }));
        const path = pathTo(s, slot, cmd.hex, reach);
        if (path === null) return fail(illegal({ code: "PATH_BLOCKED", atIndex: 0 }));
        travelled = reach.dist[cmd.hex]!;
        const walked = crossHazards(c, content, slot, path);
        const stopped = walked[walked.length - 1]!;
        moveStack(c, slot, stopped, walked, target);
        applyHazardOnEnter(c, content, slot, stopped);
        if (!isAlive(st, slot)) { endAction(c, content, slot, false); return Ok(true); }
        // A mine on the way ended the charge short of the target: it was a
        // move, and the blow is not struck from where the stack now stands.
        if (stopped !== cmd.hex || !stacksAdjacent(st, slot, target)) {
          endAction(c, content, slot, true);
          return Ok(true);
        }
      } else if (!stacksAdjacent(st, slot, target)) {
        return fail(illegal({ code: "NOT_ADJACENT" }));
      }

      resolveAttack(c, content, slot, target, false, travelled, from, magic);
      endAction(c, content, slot, true);
      return Ok(true);
    }

    // ------------------------------------------------------------------- shoot
    case "BATTLE_SHOOT": {
      const target = cmd.target;
      if (!canShoot(st, slot)) return fail(illegal({ code: "TARGET_INVALID" }));
      if ((st.flags2[slot]! & F2_AFRAID) !== 0) return fail(illegal({ code: "TARGET_INVALID" }));
      if (st.shots[slot]! <= 0) return fail(illegal({ code: "NO_SHOTS_LEFT" }));
      if (!isAlive(st, target)) return fail(illegal({ code: "STACK_DEAD", stack: target }));
      if (st.side[target] === st.side[slot]) return fail(illegal({ code: "TARGET_INVALID" }));
      if (isUntargetable(st, target)) return fail(illegal({ code: "TARGET_INVALID" }));
      if (meleeBlocked(s, slot)) return fail(illegal({ code: "NOT_ADJACENT" }));
      spendShot(c, slot);
      resolveAttack(c, content, slot, target, true, 0, -1);
      endAction(c, content, slot, true);
      return Ok(true);
    }

    // ------------------------------------------------------------- war machines
    case "BATTLE_CATAPULT": {
      if (!canBatter(st, slot)) return fail(illegal({ code: "TARGET_INVALID" }));
      if (s.siege === null || !s.siege.active) return fail(illegal({ code: "TARGET_INVALID" }));
      const seg = cmd.wallSegment;
      if (!Number.isInteger(seg) || seg < 0 || seg >= s.obstacles.length) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      const o = s.obstacles[seg]!;
      if ((o.kind !== "wall" && o.kind !== "gate" && o.kind !== "tower") || o.integrity <= 0) {
        return fail(illegal({ code: "TARGET_INVALID" }));
      }
      batterVolley(c, seg, batterCrewLevel(st, slot));
      endAction(c, content, slot, false);
      return Ok(true);
    }

    case "BATTLE_HEAL": {
      const target = cmd.target;
      if (!isAlive(st, target)) return fail(illegal({ code: "STACK_DEAD", stack: target }));
      if (st.side[target] !== st.side[slot]) return fail(illegal({ code: "TARGET_INVALID" }));
      if (!canTend(st, slot)) return fail(illegal({ code: "TARGET_INVALID" }));
      healStack(c, target, tentHeal(s, slot), st.count[target]!);
      endAction(c, content, slot, false);
      return Ok(true);
    }

  }
}

// ----------------------------------------------------------------- utilities

/** Open the battle: run the turn machine up to the first commandable stack. */
export function openBattle(state: BattleState, content: ContentRegistry): readonly GameEvent[] {
  const events: GameEvent[] = [];
  const c: Ctx = { s: state, j: getJournal(state), events };
  events.push({
    type: "BattleStarted",
    attacker: state.heroes[0].owner,
    defender: state.heroes[1].owner,
    terrain: state.terrain,
    siege: state.siege !== null && state.siege.active,
  });
  events.push({ type: "BattleRoundStarted", round: state.round });
  for (let i = 0; i < state.stacks.n; i++) onRoundStartAbilities(c, content, i);
  perkRoundStart(c, content);
  settle(c, content);
  return events;
}
